import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';

interface DailyReportFormData {
  woContractNo: string;
  woNo: string;
  contractNo: string;
  woPeriod: string;
  date: string;
  project: string;
  items: Array<{
    item: string;
    description: string;
    quantity: string;
    sampleIdentification: string;
  }>;
  temperature: {
    tank1Max: string;
    tank1Min: string;
    tank2Max: string;
    tank2Min: string;
    tank3Max: string;
    tank3Min: string;
    tank4Max: string;
    tank4Min: string;
    centralMax: string;
    centralMin: string;
    roomMax: string;
    roomMin: string;
  };
}

const DailyReportForm: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<DailyReportFormData>({
    woContractNo: '',
    woNo: '',
    contractNo: '',
    woPeriod: '',
    date: new Date().toISOString().split('T')[0],
    project: '',
    items: Array.from({ length: 10 }, () => ({
      item: '',
      description: '',
      quantity: '',
      sampleIdentification: ''
    })),
    temperature: {
      tank1Max: '',
      tank1Min: '',
      tank2Max: '',
      tank2Min: '',
      tank3Max: '',
      tank3Min: '',
      tank4Max: '',
      tank4Min: '',
      centralMax: '',
      centralMin: '',
      roomMax: '',
      roomMin: ''
    }
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleTemperatureChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      temperature: {
        ...prev.temperature,
        [field]: value
      }
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        item: '',
        description: '',
        quantity: '',
        sampleIdentification: ''
      }]
    }));
  };

  const handleSubmit = () => {
    console.log('Form submitted:', formData);
    // 這裡可以添加提交邏輯
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: 'linear-gradient(135deg,rgb(255, 255, 255) 0%, #f3e5f5 100%)',
          borderRadius: 2
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: '#1976d2',
              mb: 2
            }}
          >
            {t('pages.dailyReport.form.title')}
          </Typography>

          {/* Search Section */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
            <TextField
              label={t('pages.dailyReport.form.woContractNo')}
              value={formData.woContractNo}
              onChange={(e) => handleInputChange('woContractNo', e.target.value)}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' }
              }}
            >
              {t('pages.dailyReport.form.search')}
            </Button>
          </Box>
        </Box>

        {/* Top Details Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('pages.dailyReport.form.woNo')}
                  value={formData.woNo}
                  onChange={(e) => handleInputChange('woNo', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('pages.dailyReport.form.contractNo')}
                  value={formData.contractNo}
                  onChange={(e) => handleInputChange('contractNo', e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('pages.dailyReport.form.woPeriod')}
                  value={formData.woPeriod}
                  onChange={(e) => handleInputChange('woPeriod', e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('pages.dailyReport.form.date')}
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CalendarIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('pages.dailyReport.form.project')}
              value={formData.project}
              onChange={(e) => handleInputChange('project', e.target.value)}
            />
          </Grid>
        </Grid>

        {/* Items Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('pages.dailyReport.form.itemDetails')}
          </Typography>

          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.dailyReport.form.item')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.dailyReport.form.description')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.dailyReport.form.quantity')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.dailyReport.form.sampleIdentification')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={item.item}
                        onChange={(e) => handleItemChange(index, 'item', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={item.sampleIdentification}
                        onChange={(e) => handleItemChange(index, 'sampleIdentification', e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addItem}
            sx={{
              mt: 2,
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' }
            }}
          >
            {t('pages.dailyReport.form.add')}
          </Button>
        </Box>

        {/* Curing Temperature Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('pages.dailyReport.form.curingTemperature')}
          </Typography>

          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.dailyReport.form.tankNo')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('pages.dailyReport.form.tank1')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('pages.dailyReport.form.tank2')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('pages.dailyReport.form.tank3')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('pages.dailyReport.form.tank4')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('pages.dailyReport.form.central')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t('pages.dailyReport.form.room')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.dailyReport.form.max')}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank1Max}
                      onChange={(e) => handleTemperatureChange('tank1Max', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank2Max}
                      onChange={(e) => handleTemperatureChange('tank2Max', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank3Max}
                      onChange={(e) => handleTemperatureChange('tank3Max', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank4Max}
                      onChange={(e) => handleTemperatureChange('tank4Max', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.centralMax}
                      onChange={(e) => handleTemperatureChange('centralMax', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.roomMax}
                      onChange={(e) => handleTemperatureChange('roomMax', e.target.value)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('pages.dailyReport.form.min')}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank1Min}
                      onChange={(e) => handleTemperatureChange('tank1Min', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank2Min}
                      onChange={(e) => handleTemperatureChange('tank2Min', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank3Min}
                      onChange={(e) => handleTemperatureChange('tank3Min', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.tank4Min}
                      onChange={(e) => handleTemperatureChange('tank4Min', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.centralMin}
                      onChange={(e) => handleTemperatureChange('centralMin', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={formData.temperature.roomMin}
                      onChange={(e) => handleTemperatureChange('roomMin', e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: '#1976d2',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            {t('pages.dailyReport.form.submit')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default DailyReportForm;
